/**
 * The tenant change channel's client (D35, `@acp/api-contract/changes.js`):
 * open the WS, send `changes.subscribe`, report every `resource.changed`,
 * keep the socket alive, and reconnect with the watch client's backoff.
 *
 * **A reconnect is a gap.** Changes made while the socket was down are not
 * replayed (the channel carries ids, not a log): every `subscribed` after the
 * first is reported with `resumed: true`, and a consumer refetches what it
 * shows (the SPA invalidates every list; `acp watch --changes` says so).
 *
 * Shared by `acp watch --changes` and the SPA's global change hook, so the
 * CLI verifies exactly what the SPA relies on (D29).
 */
import type { ResourceChange } from '@acp/api-contract/changes.js';
import type { ContractParsing } from '@acp/api-contract/parsing.js';
import { parseServerMessage, type ServerMessage } from '@acp/api-contract/ws.js';
import type { SocketFactory, SocketLike, Timers } from './ports.js';
import { backoffMs, KEEPALIVE_MS } from './watchClient.js';

export type ChangesWatchEvent =
  /** Subscribed: `resumed` after a reconnect (changes may have been missed — refetch). */
  | { readonly type: 'subscribed'; readonly resumed: boolean }
  | { readonly type: 'change'; readonly change: ResourceChange; readonly ts: number }
  | { readonly type: 'nack'; readonly message: Extract<ServerMessage, { type: 'nack' }> }
  | { readonly type: 'connecting' }
  | { readonly type: 'disconnected'; readonly code: number; readonly reason: string; readonly retryInMs: number }
  | { readonly type: 'warning'; readonly message: string };

export type ChangesWatchOptions = {
  /** Read at every (re)connect: after a failover it names the new primary. */
  readonly wsUrl: () => string;
  /** The envelope's routing hint (the server acts on the token's tenant, D13). */
  readonly tenantId: string;
  readonly token: () => Promise<string>;
  readonly openSocket: SocketFactory;
  readonly timers: Timers;
  readonly nowMs: () => number;
  readonly parsing: ContractParsing;
  readonly onEvent: (event: ChangesWatchEvent) => void;
};

export type ChangesWatchHandle = {
  readonly stop: () => void;
  /** Close the socket and connect again now (a failover: the primary moved). */
  readonly reconnect: () => void;
  /** Resolves after `stop()`; rejects if (re)connecting fails fatally (no token). */
  readonly done: Promise<void>;
};

export function watchChanges(opts: ChangesWatchOptions): ChangesWatchHandle {
  let stopped = false;
  let socket: SocketLike | null = null;
  let attempt = 0;
  let subscribedOnce = false;
  /** Bumped by every (re)connect: a connect still awaiting its token when a newer one started opens nothing. */
  let generation = 0;
  let cancelKeepalive: (() => void) | null = null;
  let cancelRetry: (() => void) | null = null;
  const { promise: done, resolve: resolveDone, reject: rejectDone } = Promise.withResolvers<void>();

  const frame = (type: 'changes.subscribe' | 'ping'): string => JSON.stringify({ type, tenantId: opts.tenantId, payload: {}, ts: opts.nowMs() });

  const onMessage = (data: unknown): void => {
    const parsed = parseServerMessage(typeof data === 'string' ? data : String(data), opts.parsing);
    if (!parsed.ok) {
      opts.onEvent({ type: 'warning', message: `unparseable frame: ${parsed.error}` });
      return;
    }
    const message = parsed.message;
    if (message.type === 'changes.subscribed') {
      attempt = 0;
      opts.onEvent({ type: 'subscribed', resumed: subscribedOnce });
      subscribedOnce = true;
    } else if (message.type === 'resource.changed') opts.onEvent({ type: 'change', change: message.payload, ts: message.ts });
    else if (message.type === 'nack') opts.onEvent({ type: 'nack', message });
  };

  const scheduleRetry = (): void => {
    const retryInMs = backoffMs(attempt);
    attempt += 1;
    cancelRetry = opts.timers.after(retryInMs, () => {
      cancelRetry = null;
      void connect();
    });
  };

  const connect = async (): Promise<void> => {
    generation += 1;
    const mine = generation;
    let token: string;
    try {
      token = await opts.token();
    } catch (err) {
      stopped = true;
      rejectDone(err);
      return;
    }
    if (stopped || mine !== generation) return;
    opts.onEvent({ type: 'connecting' });
    const s = opts.openSocket(`${opts.wsUrl()}?token=${encodeURIComponent(token)}`);
    socket = s;
    // This socket's own keepalive: a replaced socket closing late must not cancel its successor's.
    let cancelOwnKeepalive: (() => void) | null = null;
    s.onopen = () => {
      s.send(frame('changes.subscribe'));
      cancelOwnKeepalive = opts.timers.every(KEEPALIVE_MS, () => s.send(frame('ping')));
      cancelKeepalive = cancelOwnKeepalive;
    };
    s.onmessage = (event) => onMessage(event.data);
    s.onerror = () => undefined; // a close follows; that is where the retry is decided
    s.onclose = (event) => {
      cancelOwnKeepalive?.();
      // A socket replaced by `reconnect` closing late is not this watch's news.
      if (socket !== s) return;
      cancelKeepalive = null;
      socket = null;
      if (stopped) return;
      opts.onEvent({ type: 'disconnected', code: event.code, reason: event.reason, retryInMs: backoffMs(attempt) });
      scheduleRetry();
    };
  };

  void connect();

  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      cancelRetry?.();
      cancelKeepalive?.();
      socket?.close();
      resolveDone();
    },
    reconnect: () => {
      if (stopped) return;
      cancelRetry?.();
      cancelRetry = null;
      cancelKeepalive?.();
      cancelKeepalive = null;
      const old = socket;
      socket = null;
      old?.close();
      attempt = 0;
      void connect();
    },
    done,
  };
}
